import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Activity, Plus, Trash2, X, RefreshCw, Zap, Sun, ThermometerSun, BarChart3, Settings } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import toast from 'react-hot-toast';

const PROVIDERS = {
  solaredge: { label: 'SolarEdge', color: '#E8432D', logo: '☀️' },
  huawei: { label: 'Huawei FusionSolar', color: '#CF0A2C', logo: '🔴' },
  fronius: { label: 'Fronius', color: '#FFD100', logo: '🟡' },
  sma: { label: 'SMA Sunny Portal', color: '#009FE3', logo: '🔵' },
  enphase: { label: 'Enphase', color: '#F37021', logo: '🟠' },
  goodwe: { label: 'GoodWe', color: '#00A651', logo: '🟢' },
  demo: { label: 'Demo (datos simulados)', color: '#D4A017', logo: '⚡' },
};

export default function Monitoring() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(null);
  const [overview, setOverview] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    provider: 'demo', apiKey: '', siteId: '', baseUrl: '', extraConfig: { capacity_kwp: '' },
  });

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.data || r.data || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    api.get('/monitoring/configs', { params: { project_id: selectedProject } })
      .then(r => { setConfigs(r.data); if (r.data[0]) selectConfig(r.data[0]); })
      .catch(() => {});
  }, [selectedProject]);

  const selectConfig = async (cfg) => {
    setActiveConfig(cfg);
    try {
      const [ovRes, dataRes] = await Promise.all([
        api.get(`/monitoring/overview/${cfg.id}`),
        api.get(`/monitoring/data/${cfg.id}`, {
          params: { from: new Date(Date.now() - 7 * 86400000).toISOString(), granularity: 'hourly' }
        }),
      ]);
      setOverview(ovRes.data);
      const grouped = {};
      (dataRes.data || []).forEach(d => {
        const day = new Date(d.timestamp).toLocaleDateString('es', { weekday: 'short', day: 'numeric' });
        if (!grouped[day]) grouped[day] = { day, energy: 0, peakPower: 0 };
        grouped[day].energy += parseFloat(d.energy_kwh || 0);
        grouped[day].peakPower = Math.max(grouped[day].peakPower, parseFloat(d.power_kw || 0));
      });
      setChartData(Object.values(grouped).map(d => ({
        ...d, energy: Math.round(d.energy * 10) / 10, peakPower: Math.round(d.peakPower * 10) / 10,
      })));
    } catch (e) { console.warn(e); }
  };

  const syncData = async () => {
    if (!activeConfig) return;
    setSyncing(true);
    try {
      const { data } = await api.post(`/monitoring/sync/${activeConfig.id}`, {
        dateFrom: new Date(Date.now() - 86400000).toISOString().split('T')[0],
        dateTo: new Date().toISOString().split('T')[0],
      });
      toast.success(`Sincronizado: ${data.inserted} puntos nuevos`);
      selectConfig(activeConfig);
    } catch (e) { toast.error('Error sincronizando'); }
    finally { setSyncing(false); }
  };

  const createConfig = async () => {
    if (!selectedProject) return toast.error('Selecciona un proyecto');
    try {
      const { data } = await api.post('/monitoring/configs', {
        projectId: selectedProject,
        provider: createForm.provider,
        apiKey: createForm.apiKey || null,
        siteId: createForm.siteId || null,
        baseUrl: createForm.baseUrl || null,
        extraConfig: createForm.extraConfig,
      });
      setConfigs(prev => [...prev, data]);
      setShowCreate(false);
      setCreateForm({ provider: 'demo', apiKey: '', siteId: '', baseUrl: '', extraConfig: { capacity_kwp: '' } });
      selectConfig(data);
      toast.success('Configuración creada');
    } catch (e) { toast.error('Error'); }
  };

  const deleteConfig = async (id) => {
    if (!window.confirm('¿Eliminar configuración?')) return;
    try {
      await api.delete(`/monitoring/configs/${id}`);
      setConfigs(prev => prev.filter(c => c.id !== id));
      if (activeConfig?.id === id) { setActiveConfig(null); setOverview(null); }
      toast.success('Eliminado');
    } catch (e) { toast.error('Error'); }
  };

  return (
    <div className="monitoring-page">
      <div className="page-header">
        <div><h1><Activity size={24} /> Monitorización</h1><p className="subtitle">Datos en tiempo real de inversores</p></div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Añadir Inversor</button>
      </div>

      <div className="filters-bar">
        <select className="filter-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
          <option value="">Seleccionar proyecto...</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
        </select>
        {configs.map(cfg => (
          <button key={cfg.id}
            className={`mon-config-btn ${activeConfig?.id === cfg.id ? 'active' : ''}`}
            onClick={() => selectConfig(cfg)}>
            {PROVIDERS[cfg.provider]?.logo} {PROVIDERS[cfg.provider]?.label || cfg.provider}
          </button>
        ))}
      </div>

      {!activeConfig && selectedProject && configs.length === 0 && (
        <div className="empty-state"><Activity size={48} /><h3>Sin configuración</h3><p>Añade un inversor para empezar a monitorizar</p></div>
      )}

      {activeConfig && overview && (
        <>
          {/* KPI Cards */}
          <div className="mon-kpi-grid">
            <div className="mon-kpi-card">
              <Zap size={20} style={{ color: '#D4A017' }} />
              <div className="mon-kpi-value">{overview.currentPower?.toFixed(1) || 0}<small> kW</small></div>
              <div className="mon-kpi-label">Potencia actual</div>
            </div>
            <div className="mon-kpi-card">
              <Sun size={20} style={{ color: '#F59E0B' }} />
              <div className="mon-kpi-value">{overview.todayEnergy?.toFixed(1) || 0}<small> kWh</small></div>
              <div className="mon-kpi-label">Energía hoy</div>
            </div>
            <div className="mon-kpi-card">
              <BarChart3 size={20} style={{ color: '#3B82F6' }} />
              <div className="mon-kpi-value">{overview.monthEnergy?.toFixed(0) || 0}<small> kWh</small></div>
              <div className="mon-kpi-label">Energía este mes</div>
            </div>
            <div className="mon-kpi-card">
              <Activity size={20} style={{ color: '#10B981' }} />
              <div className="mon-kpi-value">{(overview.totalEnergy / 1000)?.toFixed(1) || 0}<small> MWh</small></div>
              <div className="mon-kpi-label">Total histórico</div>
            </div>
          </div>

          {/* Sync button */}
          <div className="mon-sync-bar">
            <button className={`btn btn-sm btn-secondary ${syncing ? 'spinning' : ''}`} onClick={syncData} disabled={syncing}>
              <RefreshCw size={14} /> {syncing ? 'Sincronizando...' : 'Sincronizar datos'}
            </button>
            {activeConfig.last_sync_at && (
              <span className="mon-sync-info">Última sincronización: {new Date(activeConfig.last_sync_at).toLocaleString('es')}</span>
            )}
            <button className="btn btn-sm btn-danger" style={{ marginLeft: 'auto' }} onClick={() => deleteConfig(activeConfig.id)}>
              <Trash2 size={14} /> Eliminar config
            </button>
          </div>

          {/* Charts */}
          {chartData.length > 0 && (
            <div className="mon-charts">
              <div className="mon-chart-card">
                <h3>Producción Diaria (kWh)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="day" stroke="#64748B" fontSize={11} />
                    <YAxis stroke="#64748B" fontSize={11} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }}
                      labelStyle={{ color: '#94a3b8' }} />
                    <Bar dataKey="energy" fill="#D4A017" name="Energía (kWh)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mon-chart-card">
                <h3>Potencia Pico (kW)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis dataKey="day" stroke="#64748B" fontSize={11} />
                    <YAxis stroke="#64748B" fontSize={11} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8 }}
                      labelStyle={{ color: '#94a3b8' }} />
                    <Area type="monotone" dataKey="peakPower" fill="rgba(212,160,23,0.2)" stroke="#D4A017" name="Pico (kW)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Configurar Inversor</h3><button className="btn-icon" onClick={() => setShowCreate(false)}><X size={18} /></button></div>
            <div className="modal-body">
              <div className="form-group">
                <label>Proyecto *</label>
                <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
                  <option value="">Seleccionar...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Proveedor</label>
                <select value={createForm.provider} onChange={e => setCreateForm(f => ({ ...f, provider: e.target.value }))}>
                  {Object.entries(PROVIDERS).map(([k, v]) => <option key={k} value={k}>{v.logo} {v.label}</option>)}
                </select>
              </div>
              {createForm.provider !== 'demo' && (
                <>
                  <div className="form-group"><label>API Key</label><input type="password" value={createForm.apiKey} onChange={e => setCreateForm(f => ({ ...f, apiKey: e.target.value }))} placeholder="Clave API" /></div>
                  <div className="form-group"><label>Site/Plant ID</label><input value={createForm.siteId} onChange={e => setCreateForm(f => ({ ...f, siteId: e.target.value }))} placeholder="Identificador del sitio" /></div>
                  <div className="form-group"><label>URL base (opcional)</label><input value={createForm.baseUrl} onChange={e => setCreateForm(f => ({ ...f, baseUrl: e.target.value }))} placeholder="https://..." /></div>
                </>
              )}
              {createForm.provider === 'demo' && (
                <div className="form-group">
                  <label>Capacidad simulada (kWp)</label>
                  <input type="number" value={createForm.extraConfig.capacity_kwp}
                    onChange={e => setCreateForm(f => ({ ...f, extraConfig: { ...f.extraConfig, capacity_kwp: e.target.value } }))}
                    placeholder="Ej: 50" />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={createConfig}>Guardar Configuración</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
